import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, IndianRupee, ShoppingBag, BarChart2, PieChart, Calendar, ArrowUp, ArrowDown, Flame } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { format, subDays, startOfDay, parseISO } from 'date-fns';

// ── Tiny progress bar ─────────────────────────────────────────────────────
const MiniBar = ({ value, max, color = '#E65100' }) => (
  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
    <div
      className="h-full rounded-full transition-all duration-700"
      style={{ width: max ? `${Math.round((value / max) * 100)}%` : '0%', backgroundColor: color }}
    />
  </div>
);

// ── Revenue bar chart ─────────────────────────────────────────────────────
const RevenueChart = ({ records, days }) => {
  const labels = Array.from({ length: days }, (_, i) => {
    const d = subDays(new Date(), days - 1 - i);
    return { date: startOfDay(d), label: days === 7 ? format(d, 'EEE') : format(d, 'd MMM') };
  });

  const data = labels.map(({ date }) =>
    records
      .filter(r => {
        try { return startOfDay(parseISO(r.created_at)).getTime() === date.getTime(); }
        catch { return false; }
      })
      .reduce((s, r) => s + (r.subtotal || 0), 0)
  );

  const max = Math.max(...data, 1);

  return (
    <div className="flex items-end gap-1 h-32 w-full">
      {data.map((val, i) => (
        <div key={i} className="flex flex-col items-center flex-1 gap-1 h-full justify-end group relative">
          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
            ₹{val.toFixed(0)}
          </div>
          <div
            className="w-full rounded-t-md transition-all duration-500 min-h-[4px]"
            style={{ height: `${Math.max((val / max) * 112, 4)}px`, backgroundColor: val === max ? '#E65100' : '#FDBA74' }}
          />
          <span className="text-[9px] text-gray-400 leading-none truncate w-full text-center">{labels[i].label}</span>
        </div>
      ))}
    </div>
  );
};

// ── Payment method breakdown (row layout, no donut — avoids overflow) ─────
const PaymentBreakdown = ({ cash, upi, card }) => {
  const total = cash + upi + card || 1;
  const rows = [
    { label: 'Cash',    value: cash, color: '#22c55e' },
    { label: 'UPI / QR', value: upi,  color: '#a855f7' },
    { label: 'Card',    value: card, color: '#3b82f6' },
  ].filter(r => r.value > 0);

  if (rows.length === 0) return <p className="text-sm text-gray-400">No data yet</p>;

  return (
    <div className="space-y-3">
      {rows.map(row => (
        <div key={row.label}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
              <span className="text-sm text-gray-600">{row.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">{Math.round((row.value / total) * 100)}%</span>
              <span className="text-sm font-semibold text-gray-900 w-20 text-right">₹{row.value.toFixed(0)}</span>
            </div>
          </div>
          <MiniBar value={row.value} max={total} color={row.color} />
        </div>
      ))}
    </div>
  );
};

// ── Order type breakdown (same row style for visual consistency) ──────────
const OrderTypeBreakdown = ({ dineIn, delivery, takeaway }) => {
  const total = dineIn + delivery + takeaway || 1;
  const rows = [
    { label: 'Dine In',   value: dineIn,    color: '#E65100' },
    { label: 'Delivery',  value: delivery,  color: '#f59e0b' },
    { label: 'Takeaway',  value: takeaway,  color: '#06b6d4' },
  ].filter(r => r.value > 0);

  if (rows.length === 0) return <p className="text-sm text-gray-400">No data yet</p>;

  return (
    <div className="space-y-3">
      {rows.map(row => (
        <div key={row.label}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
              <span className="text-sm text-gray-600">{row.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">{Math.round((row.value / total) * 100)}%</span>
              <span className="text-sm font-semibold text-gray-900 w-16 text-right">{row.value} orders</span>
            </div>
          </div>
          <MiniBar value={row.value} max={total} color={row.color} />
        </div>
      ))}
    </div>
  );
};

// ── Main Analytics ────────────────────────────────────────────────────────
const Analytics = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(7);

  useEffect(() => {
    api.getBillingRecords()
      .then(res => setRecords(res.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  // Filter to selected date range
  const filtered = useMemo(() => {
    const cutoff = startOfDay(subDays(new Date(), range - 1));
    return records.filter(r => {
      try { return parseISO(r.created_at) >= cutoff; } catch { return false; }
    });
  }, [records, range]);

  // KPIs
  const totalRevenue = filtered.reduce((s, r) => s + (r.subtotal || 0), 0);
  const totalOrders  = filtered.length;
  const avgOrderValue = totalOrders ? totalRevenue / totalOrders : 0;

  // Revenue change vs previous period
  const prevCutoff = startOfDay(subDays(new Date(), range * 2 - 1));
  const prevEnd    = startOfDay(subDays(new Date(), range));
  const prevFiltered = records.filter(r => {
    try { const d = parseISO(r.created_at); return d >= prevCutoff && d < prevEnd; }
    catch { return false; }
  });
  const prevRevenue = prevFiltered.reduce((s, r) => s + (r.subtotal || 0), 0);
  const revChange   = prevRevenue ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : null;

  // Payment methods: cash, upi, card
  const cashTotal = filtered.filter(r => r.payment_method === 'cash').reduce((s, r) => s + (r.subtotal || 0), 0);
  const upiTotal  = filtered.filter(r => r.payment_method === 'upi').reduce((s, r) => s + (r.subtotal || 0), 0);
  const cardTotal = filtered.filter(r => r.payment_method === 'card').reduce((s, r) => s + (r.subtotal || 0), 0);

  // Order type split
  const dineInCount   = filtered.filter(r => r.order_type === 'dine_in').length;
  const deliveryCount = filtered.filter(r => r.order_type === 'delivery').length;
  const takeawayCount = filtered.filter(r => r.order_type === 'takeaway').length;

  // Item-wise sales
  const itemSales = useMemo(() => {
    const map = {};
    filtered.forEach(record => {
      (record.items || []).forEach(item => {
        if (!map[item.name]) map[item.name] = { name: item.name, qty: 0, revenue: 0 };
        map[item.name].qty     += item.quantity;
        map[item.name].revenue += item.price * item.quantity;
      });
    });
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [filtered]);

  const maxQty = itemSales[0]?.qty || 1;

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 w-40 bg-gray-200 rounded mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1,2,3].map(i => <div key={i} className="h-28 bg-gray-200 rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Analytics</h2>
            <p className="text-gray-500 mt-1">Sales performance and insights</p>
          </div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {[7, 30].map(d => (
              <button
                key={d}
                onClick={() => setRange(d)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${range === d ? 'bg-white text-[#E65100] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {d === 7 ? 'Last 7 days' : 'Last 30 days'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-orange-100 rounded-lg"><IndianRupee className="w-4 h-4 text-[#E65100]" /></div>
                <span className="text-sm text-gray-500">Total Revenue</span>
              </div>
              {revChange !== null && (
                <span className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-1 rounded-full ${revChange >= 0 ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                  {revChange >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {Math.abs(revChange).toFixed(1)}%
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-gray-900">₹{totalRevenue.toFixed(0)}</p>
            <p className="text-xs text-gray-400 mt-1">vs previous {range} days</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg"><ShoppingBag className="w-4 h-4 text-blue-600" /></div>
              <span className="text-sm text-gray-500">Total Orders</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalOrders}</p>
            <p className="text-xs text-gray-400 mt-1">bills generated</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-purple-100 rounded-lg"><TrendingUp className="w-4 h-4 text-purple-600" /></div>
              <span className="text-sm text-gray-500">Avg Order Value</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">₹{avgOrderValue.toFixed(0)}</p>
            <p className="text-xs text-gray-400 mt-1">per bill</p>
          </div>
        </div>

        {/* FIX #2 — Revenue chart is now its own full-width card so it never clashes
            with payment/order-type cards on 30-day view */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-[#E65100]" />
            Revenue — Last {range} Days
          </h3>
          {filtered.length === 0
            ? <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No data yet for this period.</div>
            : <RevenueChart records={filtered} days={range} />
          }
        </div>

        {/* Payment + Order Type — each in its own card side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-4 h-4 text-purple-500" />
              Payment Methods
            </h3>
            <PaymentBreakdown cash={cashTotal} upi={upiTotal} card={cardTotal} />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#E65100]" />
              Order Type
            </h3>
            <OrderTypeBreakdown dineIn={dineInCount} delivery={deliveryCount} takeaway={takeawayCount} />
          </div>
        </div>

        {/* Item-wise Sales */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#E65100]" />
              Item-wise Sales
              <span className="text-xs text-gray-400 font-normal ml-1">sorted by quantity sold</span>
            </h3>
            <span className="text-xs text-gray-400">{itemSales.length} items</span>
          </div>

          {itemSales.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">No sales data for this period.</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {itemSales.map((item, i) => (
                <div key={item.name} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-400'}`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
                      <span className="text-sm font-semibold text-[#E65100] ml-4">₹{item.revenue.toFixed(0)}</span>
                    </div>
                    <MiniBar value={item.qty} max={maxQty} color={i === 0 ? '#E65100' : '#FDBA74'} />
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-gray-900">{item.qty}</span>
                    <p className="text-xs text-gray-400">sold</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {itemSales.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
              Based on {totalOrders} bills in the last {range} days
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Analytics;
